import type { ListBuilderConfiguration } from '../types';

/**
 * Trigger type value mapping
 */
const TRIGGER_TYPE_VALUE_MAPPING = [
	{
		value: 'event',
		label: 'Event',
		color: { bg: '#dbeafe', text: '#1e40af' },
	},
	{
		value: 'scheduled',
		label: 'Scheduled',
		color: { bg: '#fef3c7', text: '#92400e' },
	},
	{
		value: 'api',
		label: 'API',
		color: { bg: '#e0e7ff', text: '#3730a3' },
	},
	{
		value: 'manual',
		label: 'Manual',
		color: { bg: '#e5e7eb', text: '#4b5563' },
	},
];

/**
 * Active status value mapping for triggers
 */
const TRIGGER_ACTIVE_STATUS_VALUE_MAPPING = [
	{
		value: true,
		label: 'Active',
		color: { bg: '#dcfce7', text: '#166534' },
	},
	{
		value: false,
		label: 'Inactive',
		color: { bg: '#e5e7eb', text: '#4b5563' },
	},
];

/**
 * Automation Triggers list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const AUTOMATION_TRIGGERS_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-automation-triggers',
			cacheTTL: 600000,
			name: 'All Triggers',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'trigger-name',
					headers: [{ id: 'trigger-name-header', text: 'Trigger' }],
					fields: [
						{
							id: 'name-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'automation_trigger',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'name',
									objectTable: 'automation_trigger',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
						},
						{
							id: 'description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'description',
									objectTable: 'automation_trigger',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					minWidth: 200,
				},
				{
					id: 'trigger-type',
					headers: [{ id: 'trigger-type-header', text: 'Type' }],
					fields: [
						{
							id: 'trigger-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'triggerType',
									objectTable: 'automation_trigger',
								},
							},
							styleConfig: {
								size: 'sm',
								valueMapping: TRIGGER_TYPE_VALUE_MAPPING,
							},
							displayStyle: 'badge',
						},
					],
					sortable: true,
					width: 120,
				},
				{
					id: 'flow',
					headers: [{ id: 'flow-header', text: 'Flow' }],
					fields: [
						{
							id: 'flow-name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'flowName',
									objectTable: 'automation_trigger',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-flow',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
						},
						{
							id: 'version-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'versionDisplay',
									objectTable: 'automation_trigger',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					minWidth: 200,
				},
				{
					id: 'status',
					headers: [{ id: 'status-header', text: 'Status' }],
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'isActive',
									objectTable: 'automation_trigger',
								},
							},
							styleConfig: {
								size: 'sm',
								valueMapping: TRIGGER_ACTIVE_STATUS_VALUE_MAPPING,
							},
							displayStyle: 'badge',
						},
					],
					sortable: true,
					width: 100,
				},
				{
					id: 'next-run',
					headers: [{ id: 'next-run-header', text: 'Next Run' }],
					fields: [
						{
							id: 'next-run-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'nextRunAt',
									objectTable: 'automation_trigger',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
								formatOptions: { dateFormat: 'datetime-short' },
							},
							displayStyle: 'date',
						},
					],
					sortable: true,
					width: 150,
				},
				{
					id: 'actions',
					headers: [{ id: 'actions-header', text: 'Actions' }],
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'view-action',
									label: 'View Details',
									iconName: 'eye',
									callbackName: 'viewTrigger',
								},
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'editTrigger',
								},
								{
									id: 'toggle-action',
									label: 'Toggle Active',
									iconName: 'toggle-on',
									callbackName: 'toggleActive',
								},
								{
									id: 'execute-action',
									label: 'Execute Now',
									iconName: 'play',
									callbackName: 'executeTrigger',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'deleteTrigger',
								},
							],
						},
					],
					sortable: false,
					width: 100,
					alignment: 'center',
					verticalAlignment: 'middle',
				},
			],
			defaultSortId: 'trigger-name-asc',
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: [
			'automationTriggerKeywords',
			'automationTriggerType',
			'automationTriggerStatus',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/automation/triggers/:id',
			objectLabel: 'Trigger',
			objectTable: 'automation_trigger',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'triggerType', fieldLabel: 'Type' },
				{ fieldName: 'isActive', fieldLabel: 'Status' },
				{ fieldName: 'nextRunAt', fieldLabel: 'Next Run' },
				{ fieldName: 'flowName', fieldLabel: 'Flow Name' },
				{ fieldName: 'versionDisplay', fieldLabel: 'Version' },
				{ fieldName: 'createdDate', fieldLabel: 'Created Date' },
				{ fieldName: 'modifiedDate', fieldLabel: 'Modified Date' },
			],
		},
	],
};
